import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AnalysisSidebarHeader from 'components/AnalysisSidebar/components/AnalysisSidebarHeader/AnalysisSidebarHeader';
import AnalysisSidebarResultsTab from 'components/AnalysisSidebar/components/AnalysisSidebarResultsTab/AnalysisSidebarResultsTab';
import AnalysisSidebarSetupTab from 'components/AnalysisSidebar/components/AnalysisSidebarSetupTab/AnalysisSidebarSetupTab';
import AnalysisSidebarTabs from 'components/AnalysisSidebar/components/AnalysisSidebarTabs/AnalysisSidebarTabs';
import ExistingScenario from 'components/AnalysisSidebar/components/ExistingScenario/ExistingScenario';
import SidePanel from 'components/SidePanel/SidePanel';
import { ANALYSIS_SIDEBAR } from 'constants/general';

import type AnalysisTab from './interfaces/AnalysisTab';
import type IAnalysisSidebar from './interfaces/IAnalysisSidebar';

import styles from './sass/AnalysisSidebar.module.scss';

function AnalysisSidebar({
  open,
  analysis,
  valueChainMapAnalysisById,
  isAnalysisDetailsLoading,
  valueChainMapAnalysisResults,
  isAnalysisResultsLoading,
  hasMoreAnalysisResults,
  valueChainMapAnalysisScenarios,
  isAnalysisScenariosLoading,
  hasMoreAnalysisScenarios,
  isCopyAnalysisResultLoading,
  isCopyAnalysisScenariosLoading,
  isDeleteAnalysisResultLoading,
  isStartImpactAnalysisLoading,
  isUpdating,
  isSavingScenario,
  boardEntities,
  onBack,
  onRenameAnalysis,
  onStartImpactAnalysis,
  followedResultId,
  openRequestId,
  openResultId,
  onSaveScenario,
  onUpdateScenario,
  onDeleteAnalysisScenario,
  onLoadAnalysisResults,
  onLoadMoreAnalysisResults,
  onLoadAnalysisScenarios,
  onLoadMoreAnalysisScenarios,
  onCopyAnalysisResult,
  onCopyAnalysisScenarios,
  onDeleteAnalysisResult,
  activeScenarioId,
  onSelectScenario,
  activeViewScenarioId,
  onViewScenario,
  activeViewResultId,
  onViewResult,
  pendingScenarioInitialData,
  onClearPendingScenarioInitialData,
}: IAnalysisSidebar): React.JSX.Element {
  const { t } = useTranslation();
  const title = analysis?.name || t(ANALYSIS_SIDEBAR.I18N_UNTITLED_ANALYSIS);
  const [activeTab, setActiveTab] = useState<AnalysisTab>(ANALYSIS_SIDEBAR.TAB_SETUP);
  const [isCopyExistingMode, setIsCopyExistingMode] = useState(false);
  const scenarios = valueChainMapAnalysisById?.scenarios;

  useEffect(() => {
    if (!open) {
      setActiveTab(ANALYSIS_SIDEBAR.TAB_SETUP);
      setIsCopyExistingMode(false);
    }
  }, [open]);

  const handleTabChange = useCallback((tab: AnalysisTab) => {
    if (activeViewResultId !== null && tab !== ANALYSIS_SIDEBAR.TAB_RESULTS) {
      onViewResult(activeViewResultId);
    }
    if (activeViewScenarioId !== null && tab !== ANALYSIS_SIDEBAR.TAB_SETUP) {
      onViewScenario(activeViewScenarioId);
    }
    setActiveTab(tab);

    if (tab !== ANALYSIS_SIDEBAR.TAB_RESULTS) {
      return;
    }

    onLoadAnalysisResults().catch(() => {
      toast.error(t(ANALYSIS_SIDEBAR.I18N_UNKNOWN_ERROR));
    });
  }, [
    activeViewResultId,
    activeViewScenarioId,
    onLoadAnalysisResults,
    onViewResult,
    onViewScenario,
    t,
  ]);

  // An explicit "open this analysis" request — the list, the assistant, a "show in panel" button
  // — lands on the results tab when it is following a run, setup otherwise. Keyed on `openRequestId`
  // rather than on `analysis` or `followedResultId` themselves, since a request that reopens the
  // analysis already showing, on the tab the user left it on, changes neither.
  useEffect(() => {
    if (!open) {
      return;
    }

    handleTabChange(
      followedResultId != null ? ANALYSIS_SIDEBAR.TAB_RESULTS : ANALYSIS_SIDEBAR.TAB_SETUP,
    );
    // Only a fresh open request moves the tab; a later change of the load handlers, or of
    // `followedResultId` alone (see the request that set it), must not pull it back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openRequestId]);

  const isStartImpactAnalysisDisabled = (
    !analysis
    || isAnalysisDetailsLoading
    || isSavingScenario
    || isUpdating
    || (scenarios?.length ?? 0) === 0
  );

  const handleStartImpactAnalysis = async () => {
    try {
      await onStartImpactAnalysis();
      handleTabChange(ANALYSIS_SIDEBAR.TAB_RESULTS);
    } catch {
      toast.error(t(ANALYSIS_SIDEBAR.I18N_UNKNOWN_ERROR));
    }
  };

  const handleCopyAnalysisResult = async (resultId: number) => {
    handleTabChange(ANALYSIS_SIDEBAR.TAB_SETUP);

    try {
      await onCopyAnalysisResult(resultId);
    } catch (error) {
      handleTabChange(ANALYSIS_SIDEBAR.TAB_RESULTS);
      throw error;
    }
  };

  const handleOpenCopyExistingMode = async () => {
    setIsCopyExistingMode(true);

    try {
      await onLoadAnalysisScenarios();
    } catch {
      toast.error(t(ANALYSIS_SIDEBAR.I18N_UNKNOWN_ERROR));
    }
  };

  const handleBack = () => {
    if (isCopyExistingMode) {
      setIsCopyExistingMode(false);
      return;
    }

    onBack();
  };

  const handleCopyAnalysisScenarios = async (scenarioIds: number[]) => {
    try {
      await onCopyAnalysisScenarios(scenarioIds);
      setIsCopyExistingMode(false);
    } catch {
      toast.error(t(ANALYSIS_SIDEBAR.I18N_UNKNOWN_ERROR));
    }
  };

  const renderContent = () => {
    if (isCopyExistingMode) {
      return (
        <ExistingScenario
          scenarios={valueChainMapAnalysisScenarios}
          boardEntities={boardEntities}
          isLoading={isAnalysisScenariosLoading}
          hasMore={hasMoreAnalysisScenarios}
          isDuplicateLoading={isCopyAnalysisScenariosLoading}
          onLoadMore={onLoadMoreAnalysisScenarios}
          onDuplicate={handleCopyAnalysisScenarios}
        />
      );
    }

    if (activeTab === ANALYSIS_SIDEBAR.TAB_SETUP) {
      return (
        <AnalysisSidebarSetupTab
          scenarios={scenarios}
          isAnalysisDetailsLoading={isAnalysisDetailsLoading}
          boardEntities={boardEntities}
          isSavingScenario={isSavingScenario}
          activeScenarioId={activeScenarioId}
          activeViewScenarioId={activeViewScenarioId}
          onSaveScenario={onSaveScenario}
          onUpdateScenario={onUpdateScenario}
          onDeleteAnalysisScenario={onDeleteAnalysisScenario}
          onSelectScenario={onSelectScenario}
          onViewScenario={onViewScenario}
          onStartImpactAnalysis={handleStartImpactAnalysis}
          isStartImpactAnalysisLoading={isStartImpactAnalysisLoading}
          isStartImpactAnalysisDisabled={isStartImpactAnalysisDisabled}
          open={open}
          analysisId={analysis?.id}
          onCopyExisting={handleOpenCopyExistingMode}
          pendingScenarioInitialData={pendingScenarioInitialData}
          onClearPendingScenarioInitialData={onClearPendingScenarioInitialData}
        />
      );
    }

    return (
      <AnalysisSidebarResultsTab
        valueChainMapAnalysisResults={valueChainMapAnalysisResults}
        isAnalysisResultsLoading={isAnalysisResultsLoading}
        hasMoreAnalysisResults={hasMoreAnalysisResults}
        isCopyAnalysisResultLoading={isCopyAnalysisResultLoading}
        isDeleteAnalysisResultLoading={isDeleteAnalysisResultLoading}
        onBackToSetup={() => handleTabChange(ANALYSIS_SIDEBAR.TAB_SETUP)}
        onLoadMoreAnalysisResults={onLoadMoreAnalysisResults}
        onCopyAnalysisResult={handleCopyAnalysisResult}
        onDeleteAnalysisResult={onDeleteAnalysisResult}
        onViewResult={onViewResult}
        activeViewResultId={activeViewResultId}
        openRequestId={openRequestId}
        openResultId={openResultId}
      />
    );
  };

  return (
    <SidePanel
      open={open}
      className={styles.container}
      ariaModal
      role={ANALYSIS_SIDEBAR.ROLE_DIALOG}
      ariaLabel={title}
      dataAnalysisId={analysis?.id}
    >
      <AnalysisSidebarHeader
        title={title}
        analysis={analysis}
        isUpdating={isUpdating}
        open={open}
        onBack={handleBack}
        onRenameAnalysis={onRenameAnalysis}
      />
      {!isCopyExistingMode ? (
        <AnalysisSidebarTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      ) : null}
      {renderContent()}
    </SidePanel>
  );
}

export default AnalysisSidebar;
